import { rufe } from './client';

/**
 * Die Betroffenenrechte (LEG-10, LEG-11, LEG-12).
 *
 * Gegenstand ist die **Person** und nicht der Kunde: Dieselbe Person kann
 * Privatkunde sein, Ansprechpartner einer Firma und Teilnehmer einer Buchung.
 * Art. 15 fragt nach ihr und nicht nach einem ihrer Konten.
 */

export type Personenzeile = {
  readonly id: string;
  readonly vorname: string | null;
  readonly nachname: string;
  readonly geburtsdatum: string | null;
  /** Wo sie im System vorkommt — zum Unterscheiden zweier Gleichnamiger. */
  readonly rollen: readonly string[];
  readonly eingeschraenktAb: string | null;
  readonly einschraenkungsgrund: string | null;
  /** Der PRIVATE Stand (LEG-12) — er wirkt nicht auf ihre Rollen in Firmen. */
  readonly werbestand: Werbestand | null;
  /** Je Firma, bei der sie Ansprechpartner ist, ein eigener Stand (seit 0319). */
  readonly firmenstaende?: readonly Firmenstand[];
};

/**
 * Der Werbestand als Ansprechpartner einer Firma (LEG-12).
 *
 * `eigener` steht an der Kontaktrolle; `wirksam` gilt in dieser Firma — ein
 * Widerspruch der Firma geht vor. Die Regel steht in der Datenbank.
 */
export type Firmenstand = {
  readonly kontaktrolle: string;
  readonly kunde: string;
  readonly kundennummer: string;
  readonly firma: string | null;
  readonly ausgeschieden: boolean;
  readonly eigener: Werbestand | null;
  readonly wirksam: Werbestand | null;
  readonly von: 'ansprechpartner' | 'firma' | null;
};

export type Werbestand = 'einwilligung' | 'ausnahme' | 'widerspruch';

/** LEG-12: Was der Stand bedeutet — er steht überall gleich. */
export const WERBESTANDNAME: Readonly<Record<Werbestand, string>> = {
  einwilligung: 'Einwilligung erteilt',
  ausnahme: 'zulässiger Ausnahmetatbestand',
  widerspruch: 'Widerspruch',
};

/** LEG-11: Was einer Löschung entgegensteht und bis wann. */
export type Loeschbefund = {
  readonly gegenstand: string;
  readonly entscheidung: 'loeschbar' | 'aufbewahrungspflicht';
  readonly grundlage: string;
  readonly bis: string | null;
  readonly anzahl: number;
};

export async function personen(suche: string): Promise<readonly Personenzeile[]> {
  const antwort = await rufe<{ readonly personen: readonly Personenzeile[] }>(
    `/datenschutz/personen?suche=${encodeURIComponent(suche)}`,
  );
  return antwort.personen;
}

/**
 * Art. 15 DSGVO: alles zu einer Person (LEG-10).
 *
 * Der Inhalt ist bewusst nicht durchtypisiert. Er wächst mit jeder Tabelle,
 * die personenbezogene Daten trägt — ein Typ hier wäre die zweite Liste
 * neben `fach.datenauskunft`, und sie wäre beim nächsten Feld unvollständig.
 * Die Maske zeigt ihn als das, was er ist: eine strukturierte Auskunft.
 */
export async function auskunft(person: string): Promise<Record<string, unknown>> {
  const antwort = await rufe<{ readonly auskunft: Record<string, unknown> }>(
    `/datenschutz/personen/${person}/auskunft`,
  );
  return antwort.auskunft;
}

export async function loeschpruefung(person: string): Promise<readonly Loeschbefund[]> {
  const antwort = await rufe<{ readonly befunde: readonly Loeschbefund[] }>(
    `/datenschutz/personen/${person}/loeschpruefung`,
  );
  return antwort.befunde;
}

/** `grund === null` gibt die Verarbeitung wieder frei (LEG-11). */
export async function einschraenkung(
  person: string,
  grund: string | null,
): Promise<{ readonly eingeschraenkt: boolean }> {
  return rufe(`/datenschutz/personen/${person}/einschraenkung`, {
    verfahren: 'POST',
    rumpf: { grund },
  });
}

export async function werbestandSetzen(
  person: string,
  stand: Werbestand,
  quelle: string,
  bemerkung?: string,
): Promise<{ readonly gesetzt: true }> {
  return rufe(`/datenschutz/personen/${person}/werbestand`, {
    verfahren: 'POST',
    rumpf: bemerkung === undefined ? { stand, quelle } : { stand, quelle, bemerkung },
  });
}

/**
 * Den Werbestand eines Ansprechpartners bei seiner Firma setzen (LEG-12).
 * Er hängt an der Kontaktrolle — weder privat noch für die Firma.
 */
export async function werbestandAmAnsprechpartnerSetzen(
  kontaktrolle: string,
  stand: Werbestand,
  quelle: string,
  bemerkung?: string,
): Promise<{ readonly gesetzt: true }> {
  return rufe(`/datenschutz/kontaktrollen/${kontaktrolle}/werbestand`, {
    verfahren: 'POST',
    rumpf: bemerkung === undefined ? { stand, quelle } : { stand, quelle, bemerkung },
  });
}

/**
 * Den Werbestand von der Kundenakte aus setzen (LEG-12).
 *
 * Woran die Erlaubnis dann hängt — an der Person des Privatkunden oder am
 * Konto der Firma —, entscheidet die Datenbank. Die Maske übergibt einen
 * Kunden und muss die Regel nicht kennen; kennte sie sie, gäbe es sie
 * zweimal.
 */
export async function werbestandAmKundenSetzen(
  kunde: string,
  stand: Werbestand,
  quelle: string,
  bemerkung?: string,
): Promise<{ readonly gesetzt: true }> {
  return rufe(`/datenschutz/kunden/${kunde}/werbestand`, {
    verfahren: 'POST',
    rumpf: bemerkung === undefined ? { stand, quelle } : { stand, quelle, bemerkung },
  });
}
